import React, { useState, useEffect, useRef } from "react";
import {
  callErrorToast,
  cleanObject,
  removeError,
} from "../../../utilities/utilities";
import { ErrorResponse } from "../../../Models/Error";
import {
  Collection,
  convertCollectionToCreate,
  convertCollectionToUpdate,
  defaultCollection,
  validate,
} from "../../../Models/Collection";
import { CollectionHandlerContext, CollectionHandlerProviderProps } from ".";
import { CollectionsService } from "../../../services/shipment/collections.ts";
import { CarrierConfigurationsService } from "../../../services/contract/carrierConfigurations.ts";
import { calculateCollectionDate } from "../utilities/index.ts";
import { CarrierConfiguration } from "../../../Models/CarrierConfiguration.ts";
import { Shipment } from "../../../Models/Shipment.ts";

/* ===========================================================================
 * SERVICE INSTANTIATION
 * =========================================================================== */
const collectionsService = new CollectionsService();
const carrierConfigurationsService = new CarrierConfigurationsService();

/* ===========================================================================
 * PROVIDER: CollectionHandlerProvider
 * =========================================================================== */

/**
 * @component CollectionHandlerProvider
 * * Orchestrates the business logic for a single Collection entity.
 * It manages initialization (fetching existing data or setting defaults),
 * validation, persistence (create/edit), and advanced workflows like
 * automatic booking and shipment-to-collection association.
 *
 * @param {CollectionHandlerProviderProps} props - Configuration for the handler context.
 * @returns {JSX.Element} A Context Provider for collection management.
 */
const CollectionHandlerProvider: React.FC<CollectionHandlerProviderProps> = ({
  children,
  id,
  action,
  title,
  carrierConfigurationId,
  standAlone = false,
  shipmentId,
  autosave,
  defaultAddressBookId,
  callback,
}) => {
  /** The current collection state (can be a partial or full entity). */
  const [data, setData] = useState<Partial<Collection>>({});

  /** List of shipments linked to this collection, used in 'details' mode. */
  const [relatedShipments, setRelatedShipments] = useState<Shipment[]>([]);

  /** Global loader for all async operations within the provider. */
  const [loader, setLoader] = useState<boolean>(true);

  /** Holds validation or API response errors. */
  const [error, setError] = useState<ErrorResponse<Collection> | null>(null);
  const prevError = useRef<ErrorResponse<Collection> | null>(null);

  /* ---------------------------------------------------------------------------
   * DATA FETCHING FUNCTIONS
   * ------------------------------------------------------------------------- */

  /**
   * Fetches a single collection by ID.
   * @async
   */
  const getCollection = async (id: string): Promise<Collection | undefined> => {
    setLoader(true);
    try {
      const res = await collectionsService.get(id);
      return res;
    } catch (err) {
      callErrorToast(err);
      return undefined;
    } finally {
      setLoader(false);
    }
  };

  /**
   * Fetches all shipments related to the current collection.
   * @async
   */
  const getRelatedShipments = async (id: string): Promise<Shipment[] | []> => {
    setLoader(true);
    try {
      const res = await collectionsService.getRelatedShipments(id);
      return res;
    } catch (err) {
      callErrorToast(err);
      return [];
    } finally {
      setLoader(false);
    }
  };

  /**
   * Retrieves carrier configuration details based on the provided ID.
   * @async
   */
  const getCarrierConfiguration = async (
    id: string
  ): Promise<CarrierConfiguration | undefined> => {
    try {
      const res = await carrierConfigurationsService.get(id);
      return res;
    } catch (err) {
      callErrorToast(err);
      return undefined;
    }
  };

  /* ---------------------------------------------------------------------------
   * PERSISTENCE LOGIC
   * ------------------------------------------------------------------------- */

  /**
   * Validates and saves the collection. Automatically handles create vs edit.
   * * @async
   * @param {Collection} collection - The collection data to save.
   * @param {keyof Collection | null} property - Field whose error should be cleared.
   * @param {boolean} save - If false, only updates local state without API call.
   * @param {boolean} isSubCall - If true, prevents internal state updates and loaders.
   */
  const saveCollection = async (
    collection: Collection,
    property: keyof Collection | null,
    save = true,
    isSubCall = false
  ) => {
    const cleaned = cleanObject(collection) as Collection;

    if (property) removeError(property, error, prevError, setError);
    if (!isSubCall) setData(cleaned);

    if (!save) return;
    if (!autosave && !isSubCall) setLoader(true);

    const validation = validate(cleaned, standAlone);
    if (!validation.isValid) {
      if (!autosave || cleaned.id) {
        setError({ response: { data: validation.errors } });
      }
      if (!isSubCall) setLoader(false);
      return;
    }

    try {
      let savedCollection: Collection;

      if (cleaned.id) {
        savedCollection = await edit(cleaned);
      } else {
        savedCollection = await create(cleaned);
      }

      if (isSubCall) return savedCollection;

      setData(savedCollection);
      callback?.(savedCollection);
    } finally {
      if (!isSubCall) setLoader(false);
    }
  };

  /**
   * Advanced workflow: Saves the collection, links a shipment (if applicable),
   * and finalizes by booking the collection.
   * * @async
   */
  const saveCollectionAndBook = async (
    collection: Collection,
    property: keyof Collection | null,
    save = true
  ) => {
    setLoader(true);
    try {
      const savedCollection = await saveCollection(
        collection,
        property,
        save,
        true // Execute as subcall to manage state manually
      );

      if (savedCollection) {
        // Link shipment if shipmentId is provided and it's not a standalone flow
        if (shipmentId && !standAlone) {
          await createShipmentRelation(savedCollection.id, shipmentId);
        }

        // Finalize by booking
        await book(savedCollection.id);

        setData(savedCollection);
        callback?.(savedCollection);
      }
    } finally {
      setLoader(false);
    }
  };

  /** Internal helper to perform create API request. */
  const create = async (collection: Collection): Promise<Collection> => {
    try {
      const payload = convertCollectionToCreate(collection);
      const created = await collectionsService.create(payload);
      return created;
    } catch (err) {
      setError(err as ErrorResponse<Collection>);
      throw err;
    }
  };

  /** Internal helper to perform update API request. */
  const edit = async (collection: Collection): Promise<Collection> => {
    try {
      const payload = convertCollectionToUpdate(collection);
      await collectionsService.edit(payload);
      return collection;
    } catch (err) {
      setError(err as ErrorResponse<Collection>);
      throw err;
    }
  };

  /** Links a shipment to a specific collection. */
  const createShipmentRelation = async (id: string, shipmentId: string) => {
    try {
      await collectionsService.createShipmentRelation(id, shipmentId);
    } catch (err) {
      callErrorToast(err);
    }
  };

  /** Finalizes a collection by booking it. */
  const book = async (id: string) => {
    try {
      await collectionsService.book(id);
    } catch (err) {
      callErrorToast(err);
    }
  };

  /* ---------------------------------------------------------------------------
   * EFFECTS
   * ------------------------------------------------------------------------- */

  /**
   * Initialization effect: loads configuration, existing data, and related shipments
   * when the component mounts or IDs change.
   */
  useEffect(() => {
    const initialize = async () => {
      setLoader(true);
      let newData: Partial<Collection> = { ...defaultCollection };
      try {
        if (carrierConfigurationId) {
          const carrierConfig = await getCarrierConfiguration(
            carrierConfigurationId
          );
          newData.carrierConfiguration = carrierConfig;
        }

        if (id) {
          const existingCollection = await getCollection(id);
          if (existingCollection) {
            newData = { ...newData, ...existingCollection };
          }

          if (action === "details") {
            const relatedShipments = await getRelatedShipments(id);
            setRelatedShipments(relatedShipments);
          }
        }
        setData(newData);
      } finally {
        setLoader(false);
      }
    };

    initialize();
  }, [id, carrierConfigurationId]);

  /**
   * Reactive calculation of the collection date based on carrier cutoff times
   * whenever the carrier configuration changes.
   */
  useEffect(() => {
    if (data.carrierConfiguration) {
      const newData = { ...data };
      newData.date = calculateCollectionDate(
        data.carrierConfiguration?.carrier?.collectionCutoffTime
      );
      const validation = validate(newData as Collection, standAlone);
      if (!validation.isValid) {
        setError({ response: { data: validation.errors } });
      }
      setData(newData);
    }
  }, [data.carrierConfiguration]);

  /* ---------------------------------------------------------------------------
   * PROVIDER RETURN
   * ------------------------------------------------------------------------- */

  return (
    <CollectionHandlerContext.Provider
      value={{
        id,
        action,
        data,
        loader,
        error,
        title,
        autosave,
        standAlone,
        defaultAddressBookId,
        relatedShipments,
        callback,
        saveCollection,
        saveCollectionAndBook,
      }}
    >
      {children}
    </CollectionHandlerContext.Provider>
  );
};

export { CollectionHandlerProvider };
